import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import type { LandingHeroData } from '../types';
import CanvasBackgroundLayer from './CanvasBackgroundLayer';
import { Button } from '../../../components/ui/button';
import { ArrowUpRight } from 'lucide-react';

interface LandingHeroProps {
    data: LandingHeroData;
}

/**
 * Hero section with dark gradient background and dual canvas layers.
 * Auth-aware CTAs: shows different buttons for logged in vs logged out users.
 */
const LandingHero: React.FC<LandingHeroProps> = ({ data }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAuthenticated = !!user;

    return (
        <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
            {/* Dark gradient background - Sonic Style (Black/Dark Grey/Orange hint) */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/20 via-black to-black" />

            {/* Blur blobs - Warm Sonic Tones */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-orange-500/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-amber-600/10 rounded-full blur-[120px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[150px]" />

            {/* Canvas background layer - z-0, full coverage */}
            <CanvasBackgroundLayer className="z-0" />

            {/* Content - z-10, clickable */}
            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
                {/* Badge */}
                <div className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm mb-8 overflow-hidden group">
                    {/* Animated border gradient - chạy */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 via-orange-500 to-amber-500 bg-[length:200%_100%] animate-gradient opacity-70"></div>
                    {/* Inner background mask */}
                    <div className="absolute inset-[2px] rounded-full bg-[var(--background-dark)]"></div>
                    {/* Content */}
                    <div className="relative z-10 flex items-center gap-2">
                        <img 
                            src="/images/logo.png" 
                            alt="Logo" 
                            className="w-8 h-8 object-contain transition-transform group-hover:scale-105 group-hover:brightness-110"
                        />
                        <span className="text-xs font-medium text-white/70 whitespace-nowrap">
                            {t('landing.hero.badge', 'By TaoThaoAI')}
                        </span>
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                    <span className="bg-gradient-to-r from-white via-violet-200 to-indigo-200 bg-clip-text text-transparent">
                        {data.title}
                    </span>
                </h1>

                {/* Subtitle */}
                <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
                    {data.subtitle}
                </p>

                {/* CTAs - Auth-aware */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    {isAuthenticated ? (
                        // LOGGED IN: Show "Open App" instead of "Get Started"
                        <>
                            <Link
                                to="/tools"
                                className="group relative overflow-hidden z-20 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic transition-all hover:brightness-110 hover:scale-105 active:scale-95"
                            >
                                <div className="flex items-center justify-center gap-x-2 bg-black/85 rounded-full group-hover:bg-black/75 transition px-8 py-4 text-base">
                                    <span className="text-white/90 font-semibold group-hover:text-white transition flex items-center gap-2">
                                        {t('landing.cta.openApp', 'Open App')}
                                        <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    </span>
                                </div>
                                {/* Shimmer Overlay */}
                                <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep" />
                                </div>
                            </Link>

                            <Button
                                asChild
                                size="lg"
                                className="relative z-20 bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 hover:border-white/50 transition-all duration-300 text-base px-8 py-6 hover:scale-105 active:scale-95"
                            >
                                <Link to="/community">
                                    {t('landing.cta.community', 'Community')}
                                </Link>
                            </Button>
                        </>
                    ) : (
                        // NOT LOGGED IN: Show "Get Started" and "View Demo"
                        <>
                            <Button
                                asChild
                                size="lg"
                                className="relative z-20 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white border-0 shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-all duration-300 text-base px-8 py-6 hover:scale-105 hover:-translate-y-1 active:scale-95"
                            >
                                <Link to="/register">
                                    {t('landing.cta.getStarted', 'Get Started Free')}
                                </Link>
                            </Button>

                            {/* Secondary CTA - Fixed styling: transparent bg with white text */}
                            <Button
                                asChild
                                size="lg"
                                className="relative z-20 bg-transparent border border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-base px-8 py-6 hover:scale-105 hover:-translate-y-1 active:scale-95"
                            >
                                <a href={data.secondaryCTA.href} target="_blank" rel="noopener noreferrer">
                                    {t('landing.cta.viewDemo', 'View Demo')}
                                </a>
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Scroll indicator - at bottom */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
                <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2 cursor-pointer hover:scale-110 hover:border-white/60 transition-all">
                    <div className="w-1 h-2 bg-white/50 rounded-full" />
                </div>
            </div>
        </section>
    );
};

export default LandingHero;
